import React, { useState, useEffect, useRef } from 'react';
import { PostGroup, Post } from '../../services/postService';
import PostGroupPreviewModal from './PostGroupPreviewModal';
import SimplePostEditModal from '../post/SimplePostEditModal';
import ScheduleModal from './ScheduleModal';
import UnscheduleModal from './UnscheduleModal';
import { MediaFile } from '../media/types';
import mediaService from '../../services/mediaService';
import AuthenticatedImage from '../media/AuthenticatedImage';
import { useClient } from '../../contexts/ClientContext';
import { MoreVertical, Trash2, Edit, Facebook, Instagram, X, Linkedin, Youtube, Clock, Calendar, Eye, Sparkles, Loader2, Send, ChevronLeft, ChevronRight } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import postService from '../../services/postService';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import moment from 'moment-timezone';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import QRCodeGenerator from '../ar/QRCode';
import PlatformIcon from '../common/PlatformIcon';
import { UpgradeModal } from '../modals/UpgradeModal';

interface PostGroupCardProps {
  postGroup: PostGroup;
  onDeleteSuccess: () => void;
  viewMode?: 'grid' | 'list';
  isSelected?: boolean;
  onSelect?: (postId: string) => void;
}

const PostGroupCard: React.FC<PostGroupCardProps> = ({ 
  postGroup, 
  onDeleteSuccess, 
  viewMode = 'grid',
  isSelected = false,
  onSelect
}) => {
  const { currentClient, canDelete, canEdit } = useClient();
  const [thumbnail, setThumbnail] = useState<MediaFile | null>(null);
  const [allMediaFiles, setAllMediaFiles] = useState<MediaFile[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Helper function to get publishing message
  const getPublishingMessage = () => {
    const allPlatforms = [...new Set(postGroup.posts.flatMap(post => post.platforms || []))];
    if (allPlatforms.length === 1) {
      return `Publishing to ${allPlatforms[0].charAt(0).toUpperCase() + allPlatforms[0].slice(1)}...`;
    }
    return 'Publishing post group...';
  };

  // Helper function to check if any posts in the group are scheduled
  const hasScheduledPosts = () => {
    return postGroup.posts?.some(post => post.status === 'scheduled') || false;
  };
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const { toast } = useToast();
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [isUnscheduleOpen, setIsUnscheduleOpen] = useState(false);
  const [previewPlatform, setPreviewPlatform] = useState<string | undefined>(undefined); // New state for selected platform
  const [isPublishing, setIsPublishing] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [postToEdit, setPostToEdit] = useState<Post | null>(null);
  const [wasPreviewOpen, setWasPreviewOpen] = useState(false);
  
  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalData, setUpgradeModalData] = useState<{
    message: string;
    currentCount?: number;
    limit?: number;
  }>({ message: '' });

  useEffect(() => {
    const fetchAllMediaFiles = async () => {
      if (!currentClient || !postGroup.posts || postGroup.posts.length === 0) return;

      const allFiles: MediaFile[] = [];
      const seenFileIds = new Set<string>(); // Track seen file IDs to avoid duplicates
      
      // Collect all media files from all posts, avoiding duplicates
      for (const post of postGroup.posts) {
        if (post.media_files && post.media_files.length > 0) {
          for (const mediaFileId of post.media_files) {
            if (!seenFileIds.has(mediaFileId)) {
              try {
                const mediaFile = await mediaService.getMediaFile(mediaFileId, currentClient.id);
                allFiles.push(mediaFile);
                seenFileIds.add(mediaFileId);
              } catch (error) {
                console.error("Failed to fetch media file:", error);
              }
            }
          }
        }
      }
      
      setAllMediaFiles(allFiles);
      if (allFiles.length > 0) {
        setThumbnail(allFiles[0]);
        setCurrentImageIndex(0);
      }
    };

    fetchAllMediaFiles();
  }, [postGroup, currentClient]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleDelete = async () => {
    if (!currentClient) return;
    try {
      await postService.deletePostGroup(postGroup.id);
      toast({
        title: "Post Group Deleted",
        description: "Your post group has been deleted successfully.",
      });
      onDeleteSuccess();
    } catch (error) {
      console.error('Failed to delete post group:', error);
      toast({
        title: "Failed to delete post group",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setShowConfirmDelete(false);
    }
  };

  const handleEdit = () => {
    navigate(`/app/post-groups/${postGroup.id}/edit`);
    setIsMenuOpen(false);
  };

  const handlePublish = async () => {
    if (!currentClient || isPublishing) return;

    setIsPublishing(true);
    setIsMenuOpen(false);

    try {
      // Publish each post to its configured platforms
      const promises = postGroup.posts.map(async (post) => {
        if (!post.media_files || post.media_files.length === 0) {
          throw new Error(`No media files found for post ${post.id}`);
        }
        
        // Get all media files for carousel support
        const mediaFiles = await Promise.all(
          post.media_files.map(fileId => mediaService.getMediaFile(fileId, currentClient.id))
        );
        
        const imageUrls = mediaFiles.map(file => file.file_url);
        
        // Publish to the post's primary platform
        const primaryPlatform = post.platforms?.[0] || 'instagram';
        return postService.publishPost(post.id, imageUrls, post.content, primaryPlatform);
      });

      if (promises.length > 0) {
        await Promise.all(promises);
        
        // Get all unique platforms from the posts
        const allPlatforms = [...new Set(postGroup.posts.flatMap(post => post.platforms || []))];
        const platformNames = allPlatforms.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
        
        toast({
          title: "Post Group Published",
          description: `Your post group has been published successfully to ${platformNames}.`,
        });
      } else {
        toast({
          title: "No Posts to Publish",
          description: "No posts found in this group.",
          variant: "destructive",
        });
      }
      
      onDeleteSuccess(); // Refetch to update status
    } catch (error: any) {
      console.error('Failed to publish post group:', error);
      
      // Check if it's a tier limit error
      if (error?.response?.status === 403 && error?.response?.data?.detail) {
        const errorDetail = error.response.data.detail;
        
        // Check if it's a monthly post limit error
        if (typeof errorDetail === 'object' && errorDetail.error === 'monthly_post_limit_exceeded') {
          setUpgradeModalData({
            message: errorDetail.message,
            currentCount: errorDetail.current_count,
            limit: errorDetail.limit
          });
          setShowUpgradeModal(true);
        } else {
          const message = typeof errorDetail === 'string' ? errorDetail : errorDetail.message || 'Publishing failed';
          toast({
            title: "Failed to publish post group",
            description: message,
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Failed to publish post group",
          description: "An unexpected error occurred while publishing.",
          variant: "destructive",
        });
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const handlePlatformIconClick = (platform: string) => {
    setPreviewPlatform(platform);
    setIsPreviewOpen(true);
  };

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (allMediaFiles.length > 1) {
      const nextIndex = (currentImageIndex + 1) % allMediaFiles.length;
      setCurrentImageIndex(nextIndex);
      setThumbnail(allMediaFiles[nextIndex]);
    }
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (allMediaFiles.length > 1) {
      const prevIndex = currentImageIndex === 0 ? allMediaFiles.length - 1 : currentImageIndex - 1;
      setCurrentImageIndex(prevIndex);
      setThumbnail(allMediaFiles[prevIndex]);
    }
  };

  const platforms = postGroup.posts.flatMap(post => post.platforms || []).filter((value, index, self) => self.indexOf(value) === index);

  const getPlatformPostStatus = (platformId: string) => {
    const post = postGroup.posts.find(p => p.platforms?.includes(platformId));
    return post ? post.status : 'draft'; // Default to draft if no post found for platform
  };

  if (viewMode === 'list') {
    return (
      <motion.div
        className={`relative bg-white dark:bg-slate-800/40 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200 dark:border-slate-600/30 flex flex-col sm:flex-row items-start transition-all duration-300 hover:shadow-2xl hover:shadow-indigo-500/10 hover:-translate-y-1 hover:border-indigo-500/50 dark:hover:shadow-indigo-500/10 dark:hover:border-indigo-500/50 cursor-pointer group p-4 ${isPublishing ? 'opacity-75' : ''} ${isMenuOpen ? 'z-50' : 'z-0'} ${isSelected ? 'ring-2 ring-indigo-500 border-indigo-500' : ''}`}
        onClick={() => !isPublishing && setIsPreviewOpen(true)}
        whileHover={{ scale: isPublishing ? 1 : 1.01 }}
        transition={{ duration: 0.2 }}
      >
        {/* Selection Checkbox */}
        {onSelect && (
          <div className="absolute top-4 left-4 z-20">
            <div
              onClick={(e) => {
                e.stopPropagation();
                onSelect(postGroup.id);
              }}
              className="cursor-pointer p-1"
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => {}} // Controlled by the div onClick
                className="w-5 h-5 rounded border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 checked:bg-indigo-500 checked:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50 transition-all duration-200 pointer-events-none"
              />
            </div>
          </div>
        )}
        {/* Publishing Overlay */}
        {isPublishing && (
          <div className="absolute inset-0 bg-indigo-500/10 backdrop-blur-sm z-10 flex items-center justify-center rounded-2xl">
            <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm px-4 py-3 rounded-xl border border-indigo-200/50 dark:border-indigo-600/50 flex items-center space-x-3">
              <Loader2 size={20} className="animate-spin text-indigo-500" />
              <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">{getPublishingMessage()}</span>
            </div>
          </div>
        )}
        {/* Image Section */}
        <div className={`w-full sm:w-64 md:w-80 lg:w-96 flex-shrink-0 mb-4 sm:mb-0 sm:mr-6 ${onSelect ? 'ml-8 sm:ml-0' : ''}`}>
          {thumbnail ? (
            <div className="aspect-w-16 aspect-h-9 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-600/30 relative group">
              <AuthenticatedImage
                src={thumbnail.thumbnail_url || thumbnail.file_url}
                alt={thumbnail.original_filename}
                className="w-full h-full object-cover"
                clientId={currentClient?.id || ''}
              />
              {allMediaFiles.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                  >
                    <ChevronRight size={16} />
                  </button>
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black/50 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    {currentImageIndex + 1} / {allMediaFiles.length}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="aspect-w-16 aspect-h-9 bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600/30">
              <span className="text-slate-400 dark:text-slate-400 text-sm">No Media</span>
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="flex-grow flex flex-col justify-between w-full">
          <div>
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white flex-grow pr-2 break-words">{postGroup.title}</h3>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-400 mt-1">
              Created: {moment(postGroup.created_at).format('MMM D, YYYY h:mm A')}
            </p>
          </div>

          {/* Footer Section */}
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-600/30 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {platforms.map(p => (
                <button key={p} onClick={(e) => { e.stopPropagation(); handlePlatformIconClick(p); }} className="p-2 rounded-full hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors duration-200">
                  <PlatformIcon platform={p} status={getPlatformPostStatus(p)} className="w-5 h-5" />
                </button>
              ))}
            </div>
            <div className="relative" ref={menuRef}>
              <button onClick={(e) => { e.stopPropagation(); setIsMenuOpen(!isMenuOpen); }} className={`focus:outline-none p-2 rounded-full hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors duration-200 relative ${isMenuOpen ? 'z-50' : 'z-10'}`}>
                <MoreVertical size={20} className="text-slate-500 dark:text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300" />
              </button>
              {isMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white/80 dark:bg-slate-800/95 backdrop-blur-xl rounded-xl shadow-2xl z-50 py-2 border border-slate-200/80 dark:border-slate-600/50 overflow-hidden">
                  <button 
                    onClick={(e) => { e.stopPropagation(); handlePublish(); }}
                    disabled={isPublishing}
                    className="block w-full text-left px-6 py-3 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
                  >
                    {isPublishing ? (
                      <Loader2 className="inline-block w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="inline-block w-4 h-4 mr-2" />
                    )}
                    {isPublishing ? 'Publishing...' : (postGroup.posts?.some(post => post.status === 'published') ? 'Re-publish Post Group' : 'Publish Post Group')}
                  </button>
                  {hasScheduledPosts() ? (
                    <button 
                      onClick={(e) => { e.stopPropagation(); setIsMenuOpen(false); setIsUnscheduleOpen(true); }}
                      className="block w-full text-left px-6 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors duration-200"
                    >
                      <Clock className="inline-block w-4 h-4 mr-2" />Unschedule
                    </button>
                  ) : (
                    <button 
                      onClick={(e) => { e.stopPropagation(); setIsMenuOpen(false); setIsScheduleOpen(true); }}
                      className="block w-full text-left px-6 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors duration-200"
                    >
                      <Clock className="inline-block w-4 h-4 mr-2" />{postGroup.posts?.some(post => post.status === 'published') ? 'Re-schedule' : 'Schedule'}
                    </button>
                  )}
                  {canDelete() && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); setIsMenuOpen(false); setShowConfirmDelete(true); }}
                      className="block w-full text-left px-6 py-2 text-sm text-red-500 dark:text-red-400 hover:bg-red-100/50 dark:hover:bg-red-900/20 transition-colors duration-200"
                    >
                      <Trash2 className="inline-block w-4 h-4 mr-2" />Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    )
  }

  return (
    <>
      <motion.div 
        className={`relative bg-white dark:bg-slate-800/40 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200 dark:border-slate-600/30 flex flex-col transition-all duration-300 hover:shadow-2xl hover:shadow-indigo-500/10 hover:-translate-y-2 hover:border-indigo-500/50 dark:hover:shadow-indigo-500/10 dark:hover:border-indigo-500/50 cursor-pointer group ${isPublishing ? 'opacity-75' : ''} ${isMenuOpen ? 'z-50' : 'z-0'} ${isSelected ? 'ring-2 ring-indigo-500 border-indigo-500' : ''}`}
        onClick={() => !isPublishing && setIsPreviewOpen(true)}
        whileHover={{ scale: isPublishing ? 1 : 1.02 }}
        transition={{ duration: 0.2 }}
      >
        {/* Selection Checkbox */}
        {onSelect && (
          <div className="absolute top-3 left-3 z-20">
            <div
              onClick={(e) => {
                e.stopPropagation();
                onSelect(postGroup.id);
              }}
              className="cursor-pointer p-1"
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => {}} // Controlled by the div onClick
                className="w-5 h-5 rounded border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 checked:bg-indigo-500 checked:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50 transition-all duration-200 pointer-events-none"
              />
            </div>
          </div>
        )}
        {/* Publishing Overlay */}
        {isPublishing && (
          <div className="absolute inset-0 bg-indigo-500/10 backdrop-blur-sm z-10 flex items-center justify-center rounded-2xl">
            <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm px-4 py-3 rounded-xl border border-indigo-200/50 dark:border-indigo-600/50 flex items-center space-x-3">
              <Loader2 size={20} className="animate-spin text-indigo-500" />
              <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">{getPublishingMessage()}</span>
            </div>
          </div>
        )}
        {/* Image Section */}
        {thumbnail ? (
          <div className="aspect-w-16 aspect-h-9 overflow-hidden rounded-t-2xl border-b border-slate-200 dark:border-slate-600/30 relative group">
            <AuthenticatedImage
              src={thumbnail.thumbnail_url || thumbnail.file_url}
              alt={thumbnail.original_filename}
              className="w-full h-full object-cover"
              clientId={currentClient?.id || ''}
            />
            {allMediaFiles.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                >
                  <ChevronRight size={16} />
                </button>
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black/50 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {currentImageIndex + 1} / {allMediaFiles.length}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="aspect-w-16 aspect-h-9 bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center rounded-t-2xl border-b border-slate-200 dark:border-slate-600/30">
            <span className="text-slate-400 dark:text-slate-400 text-sm">No Media</span>
          </div>
        )}

        {/* Content Section */}
        <div className="p-3 sm:p-4 flex-grow flex flex-col">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white flex-grow pr-2 break-words">{postGroup.title}</h3>
            <div className="relative" ref={menuRef}>
              <button onClick={(e) => { e.stopPropagation(); setIsMenuOpen(!isMenuOpen); }} className={`focus:outline-none p-1 sm:p-2 rounded-full hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors duration-200 relative ${isMenuOpen ? 'z-50' : 'z-10'}`}>
                <MoreVertical size={20} className="text-slate-500 dark:text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300" />
              </button>
              <AnimatePresence>
              {isMenuOpen && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -10 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-40 sm:w-48 bg-white/80 dark:bg-slate-800/95 backdrop-blur-xl rounded-xl shadow-2xl z-50 py-2 border border-slate-200/80 dark:border-slate-600/50 overflow-hidden"
                >
                  <button 
                    onClick={(e) => { e.stopPropagation(); handlePublish(); }}
                    disabled={isPublishing}
                    className="block w-full text-left px-4 sm:px-6 py-2 text-xs sm:text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
                  >
                    {isPublishing ? (
                      <Loader2 className="inline-block w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Instagram className="inline-block w-4 h-4 mr-2" />
                    )}
                    {isPublishing ? 'Publishing...' : (postGroup.posts?.some(post => post.status === 'published') ? 'Re-publish' : 'Publish')}
                  </button>
                  {hasScheduledPosts() ? (
                    <button 
                      onClick={(e) => { e.stopPropagation(); setIsMenuOpen(false); setIsUnscheduleOpen(true); }}
                      className="block w-full text-left px-4 sm:px-6 py-2 text-xs sm:text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors duration-200 flex items-center"
                    >
                      <Clock className="inline-block w-4 h-4 mr-2" />Unschedule
                    </button>
                  ) : (
                    <button 
                      onClick={(e) => { e.stopPropagation(); setIsMenuOpen(false); setIsScheduleOpen(true); }}
                      className="block w-full text-left px-4 sm:px-6 py-2 text-xs sm:text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors duration-200 flex items-center"
                    >
                      <Clock className="inline-block w-4 h-4 mr-2" />{postGroup.posts?.some(post => post.status === 'published') ? 'Re-schedule' : 'Schedule'}
                    </button>
                  )}
                  <div className="my-1 h-px bg-slate-200 dark:bg-slate-600/50"></div>
                  {canDelete() && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); setIsMenuOpen(false); setShowConfirmDelete(true); }}
                      className="block w-full text-left px-4 sm:px-6 py-2 text-xs sm:text-sm text-red-500 dark:text-red-400 hover:bg-red-100/50 dark:hover:bg-red-900/20 transition-colors duration-200 flex items-center"
                    >
                      <Trash2 className="inline-block w-4 h-4 mr-2" />Delete
                    </button>
                  )}
                </motion.div>
              )}
              </AnimatePresence>
            </div>
          </div>

          {/* Footer Section */}
          <div className="mt-auto pt-3 sm:pt-4 border-t border-slate-200 dark:border-slate-600/30 flex items-center justify-between">
            <div className="flex items-center space-x-1 sm:space-x-2">
              {platforms.map(p => (
                <button key={p} onClick={(e) => { e.stopPropagation(); handlePlatformIconClick(p); }} className="p-1 sm:p-2 rounded-full hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors duration-200">
                  <PlatformIcon platform={p} status={getPlatformPostStatus(p)} className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-400 dark:text-slate-400 mt-2 truncate">
            {moment(postGroup.created_at).format('MMM D, YYYY')}
          </p>
        </div>
      </motion.div>
      {isPreviewOpen && (
        <PostGroupPreviewModal
          postGroup={postGroup}
          onClose={() => setIsPreviewOpen(false)}
          initialPlatformFilter={previewPlatform} // Pass the selected platform
          onDeleteSuccess={onDeleteSuccess} // Pass the onDeleteSuccess prop
          onEditPost={(post) => {
            setPostToEdit(post);
            setIsEditModalOpen(true);
            setWasPreviewOpen(true);
            setIsPreviewOpen(false); // Temporarily close preview modal
          }}
        />
      )}

      {/* Edit Modal */}
      {postToEdit && (
        <SimplePostEditModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setPostToEdit(null);
            // Reopen preview modal if it was open before
            if (wasPreviewOpen) {
              setIsPreviewOpen(true);
              setWasPreviewOpen(false);
            }
          }}
          post={postToEdit}
          onSuccess={() => {
            setIsEditModalOpen(false);
            setPostToEdit(null);
            onDeleteSuccess(); // Refresh data first
            // Small delay to ensure data is refreshed before reopening modal
            setTimeout(() => {
              if (wasPreviewOpen) {
                setIsPreviewOpen(true);
                setWasPreviewOpen(false);
              }
            }, 100);
          }}
        />
      )}

      {isScheduleOpen && (
        <ScheduleModal
          postGroup={postGroup}
          onClose={() => setIsScheduleOpen(false)}
          onSuccess={() => {onDeleteSuccess(); setIsScheduleOpen(false);}}
        />
      )}

      {isUnscheduleOpen && (
        <UnscheduleModal
          postGroup={postGroup}
          onClose={() => setIsUnscheduleOpen(false)}
          onSuccess={() => {onDeleteSuccess(); setIsUnscheduleOpen(false);}}
        />
      )}

      {showConfirmDelete && (
        <Dialog open={showConfirmDelete} onOpenChange={setShowConfirmDelete}>
          <DialogContent className="bg-white dark:bg-slate-800/95 backdrop-blur-xl border-slate-200/50 dark:border-slate-600/30 w-full max-w-md">
            {/* Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 via-transparent to-red-500/5 rounded-2xl"></div>
            
            <DialogHeader className="relative pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-white" />
                </div>
                <DialogTitle className="text-xl font-bold text-slate-800 dark:text-slate-200">
                  Delete Post Group
                </DialogTitle>
              </div>
            </DialogHeader>
            
            <div className="relative space-y-4">
              {/* Message */}
              <div>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                  Are you sure you want to delete <span className="font-semibold text-slate-800 dark:text-slate-200">"{postGroup.title}"</span> and all its posts?
                </p>
                <p className="text-sm text-red-600 dark:text-red-400 mt-2 font-medium">
                  This action cannot be undone.
                </p>
              </div>
              
              {/* Post Count Info */}
              <div className="p-3 bg-slate-50/50 dark:bg-slate-700/30 rounded-xl border border-slate-200/50 dark:border-slate-600/30">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  This will permanently delete <span className="font-semibold text-slate-800 dark:text-slate-200">{postGroup.posts.length}</span> post{postGroup.posts.length !== 1 ? 's' : ''} in this group.
                </p>
              </div>
            </div>
            
            <DialogFooter className="relative pt-4 border-t border-slate-200/50 dark:border-slate-600/30">
              <div className="flex space-x-3 w-full">
                <Button
                  onClick={() => setShowConfirmDelete(false)}
                  variant="outline"
                  className="flex-1 bg-slate-100/50 hover:bg-slate-200/50 dark:bg-slate-700/50 border-slate-300/70 dark:border-slate-600/50 dark:hover:bg-slate-600/50 text-slate-700 dark:text-slate-300 hover:scale-105 transition-all duration-200"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg hover:shadow-red-500/25 hover:scale-105 transition-all duration-200"
                >
                  Delete Group
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Upgrade Modal */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        title="Monthly Post Limit Reached"
        message={upgradeModalData.message}
        currentCount={upgradeModalData.currentCount}
        limit={upgradeModalData.limit}
        feature="posts"
      />
    </>
  );
};

export default PostGroupCard;